import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../../../i18n/LanguageSwitcher';
import { SOURCES } from '../data/sources';
import { HORIZON_ENABLED, LIVE_SERVICE_ENABLED } from '../lib/opsFlags';
import type { ConnectedSourceId } from '../types';
import { BrandMark } from './shared/BrandMark';
import { Icon } from './shared/Icon';
import { OpsHoverTipPortal, useOpsHoverTip } from './shared/OpsHoverTip';
import { GlowingOrb } from './assistant/GlowingOrb';
import type { OpsMode } from '../hooks/useOpsCommand';
import type { GrowthNavSection } from '../data/growthViewModel';
import { UNKNOWN_GROWTH_NAV } from './growth/growthNavState';
import { GrowthNavGroup } from './GrowthNavGroup';

export function NavRail({
  mode,
  setMode,
  connected,
  onOpenIntegrations,
  onOpenHelp,
  expanded,
  onToggleExpanded,
  collapseLocked = false,
  collapseLockedReason,
  growthSections = UNKNOWN_GROWTH_NAV.sections,
  growthSection = null,
  growthAttention = false,
  onGrowthSection,
  hotelAvailable = false,
}: {
  mode: OpsMode;
  setMode: (m: OpsMode) => void;
  connected: ConnectedSourceId[];
  onOpenIntegrations: () => void;
  onOpenHelp?: () => void;
  expanded: boolean;
  onToggleExpanded: () => void;
  /** When true, width is forced collapsed; toggle still updates prefs. */
  collapseLocked?: boolean;
  collapseLockedReason?: string;
  /** Growth's sections, listed under Growth (they replace its in-page tabs). */
  growthSections?: readonly GrowthNavSection[];
  /** The Growth section on display. */
  growthSection?: string | null;
  /** Data health needs action: a quiet dot on its item. */
  growthAttention?: boolean;
  /** Opens Growth on one of its sections. */
  onGrowthSection?: (section: GrowthNavSection) => void;
  /** A venue in scope has a hotel whose PMS sends its stays: show Hotel. */
  hotelAvailable?: boolean;
}) {
  const { t } = useTranslation('nav');
  const { t: tCommon } = useTranslation('common');
  const lockedReason = collapseLockedReason ?? t('collapseLocked');
  const tip = useOpsHoverTip();
  const railRef = useRef<HTMLElement | null>(null);
  const brandRef = useRef<HTMLDivElement | null>(null);

  // Expanded width = brand mark + wordmark + rail horizontal padding.
  useLayoutEffect(() => {
    if (!expanded) return;
    const rail = railRef.current;
    const brand = brandRef.current;
    const root = rail?.closest('.ops-command');
    if (!(root instanceof HTMLElement) || !rail || !brand) return;
    const styles = getComputedStyle(rail);
    const padX =
      (parseFloat(styles.paddingLeft) || 0) +
      (parseFloat(styles.paddingRight) || 0);
    const width = Math.ceil(brand.scrollWidth + padX);
    if (width > 0) {
      root.style.setProperty('--ops-nav-w-expanded', `${width}px`);
    }
  }, [expanded]);

  // Collapsed rail: tips sit beside the icons instead of covering the next one.
  const tipPlacement = expanded ? 'below' : 'right';
  useEffect(() => {
    tip.hide();
  }, [expanded, tip.hide]);

  /** Collapsed rail: the tab's name in a tip beside its icon. */
  const railTip = (label: string) => {
    if (expanded) return {};
    const show = (e: React.SyntheticEvent<HTMLElement>) =>
      tip.showAnchored(e.currentTarget, { text: label }, 'right');
    return {
      'aria-label': label,
      onMouseEnter: show,
      onMouseLeave: tip.hide,
      onFocus: show,
      onBlur: tip.hide,
    };
  };

  const showLiveComingSoon = (el: HTMLElement) => {
    tip.showAnchored(
      el,
      { title: t('comingSoonTitle'), text: t('comingSoonBody') },
      tipPlacement,
    );
  };
  const hideLiveComingSoon = () => {
    tip.hide();
  };

  return (
    <aside
      ref={railRef}
      className={`nav-rail${expanded ? ' is-expanded' : ' is-collapsed'}${
        collapseLocked ? ' is-collapse-locked' : ''
      }`}
      aria-label={t('aria')}
    >
      <div className="nav-rail-brand" ref={brandRef}>
        <BrandMark className="nav-rail-mark" />
        {expanded && (
          <span className="nav-rail-word">
            <span className="brand-prefix">aleno</span> {tCommon('brandWordmark')}
          </span>
        )}
      </div>

      <nav className="nav-rail-modes" aria-label={t('views')} data-ftue="nav-modes">
        <button
          type="button"
          className={`nav-rail-item nav-rail-assistant${mode === 'assistant' ? ' on' : ''}`}
          onClick={() => setMode('assistant')}
          aria-current={mode === 'assistant' ? 'page' : undefined}
          {...railTip(t('assistant'))}
        >
          <GlowingOrb
            size="nav"
            state={mode === 'assistant' ? 'listening' : 'idle'}
          />
          {expanded && <span className="nav-rail-label">{t('assistant')}</span>}
        </button>
        <button
          type="button"
          className={`nav-rail-item${mode === 'performance' ? ' on' : ''}`}
          onClick={() => setMode('performance')}
          aria-current={mode === 'performance' ? 'page' : undefined}
          {...railTip(t('performance'))}
        >
          <Icon name="trendUp" size={15} />
          {expanded && <span className="nav-rail-label">{t('performance')}</span>}
        </button>
        <button
          type="button"
          className={`nav-rail-item${mode === 'horizon' ? ' on' : ''}${
            !HORIZON_ENABLED ? ' is-disabled' : ''
          }`}
          onClick={() => {
            if (!HORIZON_ENABLED) return;
            setMode('horizon');
          }}
          aria-disabled={!HORIZON_ENABLED}
          aria-current={mode === 'horizon' ? 'page' : undefined}
          {...railTip(t('forecast'))}
        >
          <Icon name="calendar" size={15} />
          {expanded && <span className="nav-rail-label">{t('forecast')}</span>}
        </button>
        {hotelAvailable && (
          <button
            type="button"
            className={`nav-rail-item${mode === 'hotel' ? ' on' : ''}`}
            onClick={() => setMode('hotel')}
            aria-current={mode === 'hotel' ? 'page' : undefined}
            {...railTip(t('hotel'))}
          >
            <Icon name="bed" size={15} />
            {expanded && <span className="nav-rail-label">{t('hotel')}</span>}
          </button>
        )}
        <GrowthNavGroup
          expanded={expanded}
          active={mode === 'growth'}
          sections={growthSections}
          current={growthSection}
          attention={growthAttention}
          tip={
            expanded
              ? undefined
              : {
                  show: el => tip.showAnchored(el, { text: t('growth') }, 'right'),
                  hide: tip.hide,
                }
          }
          onSelect={section => {
            if (section && onGrowthSection) onGrowthSection(section);
            else setMode('growth');
          }}
        />
        <button
          type="button"
          className={`nav-rail-item${mode === 'live' ? ' on' : ''}${
            !LIVE_SERVICE_ENABLED ? ' is-disabled' : ''
          }`}
          onClick={() => {
            if (!LIVE_SERVICE_ENABLED) return;
            setMode('live');
          }}
          aria-disabled={!LIVE_SERVICE_ENABLED}
          aria-current={mode === 'live' ? 'page' : undefined}
          {...(LIVE_SERVICE_ENABLED
            ? railTip(t('live'))
            : {
                'aria-label': expanded ? undefined : t('live'),
                onMouseEnter: (e: React.MouseEvent<HTMLElement>) =>
                  showLiveComingSoon(e.currentTarget),
                onMouseLeave: hideLiveComingSoon,
                onFocus: (e: React.FocusEvent<HTMLElement>) =>
                  showLiveComingSoon(e.currentTarget),
                onBlur: hideLiveComingSoon,
              })}
        >
          <Icon name="activity" size={15} />
          {expanded && <span className="nav-rail-label">{t('live')}</span>}
        </button>
        <button
          type="button"
          className={`nav-rail-item${mode === 'settings' ? ' on' : ''}`}
          onClick={() => setMode('settings')}
          aria-current={mode === 'settings' ? 'page' : undefined}
          {...railTip(t('settings'))}
        >
          <Icon name="sliders" size={15} />
          {expanded && <span className="nav-rail-label">{t('settings')}</span>}
        </button>
      </nav>

      <div className="nav-rail-spacer" />

      <div className="nav-rail-tools">
        <div className="nav-rail-lang">
          <LanguageSwitcher compact={!expanded} />
        </div>
        {onOpenHelp && (
          <button
            type="button"
            className="nav-rail-item nav-rail-help"
            data-ftue="nav-help"
            onClick={() => {
              tip.hide();
              onOpenHelp();
            }}
            onMouseEnter={e =>
              tip.showAnchored(
                e.currentTarget,
                { title: t('help'), text: t('helpTip') },
                tipPlacement,
              )
            }
            onMouseLeave={tip.hide}
            aria-label={t('help')}
          >
            <Icon name="helpCircle" size={15} />
            {expanded && <span className="nav-rail-label">{t('help')}</span>}
          </button>
        )}
        <button
          type="button"
          className="nav-rail-item nav-rail-sources"
          data-ftue="nav-sources"
          onClick={() => {
            tip.hide();
            onOpenIntegrations();
          }}
          onMouseEnter={e =>
            tip.showAnchored(
              e.currentTarget,
              {
                title: t('sources'),
                text: t('sourcesTip', {
                  connected: connected.length,
                  total: SOURCES.length,
                }),
              },
              tipPlacement,
            )
          }
          onMouseLeave={tip.hide}
          aria-label={t('sourcesAria', {
            connected: connected.length,
            total: SOURCES.length,
          })}
        >
          <Icon name="database" size={15} />
          {expanded && <span className="nav-rail-label">{t('sources')}</span>}
        </button>
      </div>

      <button
        type="button"
        className="nav-rail-item nav-rail-collapse"
        onClick={onToggleExpanded}
        aria-expanded={expanded}
        {...railTip(collapseLocked ? lockedReason : t('expand'))}
        aria-label={expanded ? t('collapseNav') : t('expandNav')}
        title={expanded ? (collapseLocked ? lockedReason : t('collapse')) : undefined}
      >
        <Icon
          name="chevron"
          size={15}
          className={`nav-rail-collapse-ic${expanded ? ' is-expanded' : ''}`}
        />
        {expanded && <span className="nav-rail-label">{t('collapse')}</span>}
      </button>

      <OpsHoverTipPortal tip={tip.tip} />
    </aside>
  );
}
